import React, { ReactElement } from 'react';

import { useIntlReexport } from 'src/application/hooks/useIntlReexport';
import { generalMessages } from 'src/translates/generalMessages';

import { ProjectViewModel } from '../../../../../config/configs';
import { StudentViewMode } from '../../../../../config/constants';
import { FeedbackModal } from '../../../../components/FeedbackModal/FeedbackModal';
import { Footer } from '../../../../components/menu/Footer/Footer';
import { MenuHeaderActions } from '../../../../components/menu/MenuHeaderActions';
import { getItemByIdSelector } from '../../../../store/reducers/items/itemsSelectors';
import { createGetResourcesFolderItemSelector } from '../../../../store/selectors/getResourcesFolderItem';
import { useStudentSelector } from '../../../../store/selectors/useStudentSelector';
import { MenuProjectProgress } from '../../containers/student/MenuProjectProgress';
import { NavigationMenuScrollWrapper } from '../NavigationMenuScrollWrapper';
import { NavigationMenuItem } from './NavigationMenuItem/NavigationMenuItem';
import { MenuItem, MenuItemLabel, MenuItemType } from '@ael/shared-components-library';

import './../navigation-menu.scss';

interface NavigationMenuProps {
  projectViewModel: ProjectViewModel;
  configurationButton?: ReactElement;
  isInPreview: boolean;
  hideStudentHeader: boolean;
  viewMode: StudentViewMode;
  backendUrl: string;
  isInHomeOverview?: boolean;
  showHomeOverview?: boolean;
  onShowHomeOverview?: () => void;
}

export const StudentNavigationMenu: React.FC<NavigationMenuProps> = ({
  projectViewModel,
  configurationButton,
  isInPreview,
  hideStudentHeader,
  backendUrl,
  viewMode,
  isInHomeOverview,
  onShowHomeOverview,
}) => {
  StudentNavigationMenu.displayName = 'StudentNavigationMenu';
  const getItemById = useStudentSelector(getItemByIdSelector);
  const { formatMessage } = useIntlReexport();

  const rootMenuItems = useStudentSelector((state) => state.rootItems);
  const resourceFolder = useStudentSelector(createGetResourcesFolderItemSelector);
  const isLinearModeOn = projectViewModel.config.isLinearModeEnabled;

  const renderItems = React.useMemo(() => {
    return rootMenuItems.map((itemId: string) => (
      <NavigationMenuItem
        key={itemId}
        itemId={itemId}
        parentItemId={null}
        nestedLevel={0}
        getItemById={getItemById}
        isLinearModeOn={isLinearModeOn}
        isStudent={true}
      />
    ));
  }, [getItemById, rootMenuItems, isLinearModeOn]);

  return (
    <nav className="navigation-menu">
      <MenuHeaderActions
        viewMode={viewMode}
        configurationButton={configurationButton}
        isInPreview={isInPreview}
        hideStudentHeader={hideStudentHeader}
      />
      <NavigationMenuScrollWrapper disableScroll={isInHomeOverview}>
        <div className="navigation-menu__project-wrapper">
          <div className="navigation-menu__project-title">{projectViewModel.config.name}</div>
          <MenuProjectProgress />
        </div>
        {projectViewModel.config.homeOverview.useOverview && onShowHomeOverview && (
          <MenuItem
            menuItemDetails={{ menuItemType: MenuItemType.HomeOverview, active: false }}
            onClick={onShowHomeOverview}
          >
            <MenuItemLabel>{formatMessage(generalMessages.overview)}</MenuItemLabel>
          </MenuItem>
        )}
        {renderItems}
      </NavigationMenuScrollWrapper>
      <div className="navigation-menu__resources-folder">
        {resourceFolder && (
          <NavigationMenuItem
            itemId={resourceFolder.id}
            parentItemId={null}
            nestedLevel={0}
            getItemById={getItemById}
            isLinearModeOn={isLinearModeOn}
            isStudent={true}
          />
        )}
      </div>
      <Footer
        watermarkText={projectViewModel.settings.watermark}
        renderFeedbackModal={renderFeedbackModal(projectViewModel, backendUrl)}
      />
    </nav>
  );
};

function renderFeedbackModal(projectViewModel: ProjectViewModel, backendUrl: string) {
  const email = projectViewModel.settings.courseFeedbackEmailAddress;
  if (email) {
    return (onClose: () => void) => (
      <FeedbackModal
        courseGuid={projectViewModel.additionalSettings.guid}
        backendUrl={backendUrl}
        projectName={projectViewModel.config.name}
        onClose={onClose}
        emailAddress={email}
        projectReference={projectViewModel.settings.reference}
      />
    );
  }

  return undefined;
}
